import { Group, Object3D, Sphere, Vector3 } from 'three';
import { EventEmitter } from './EventEmitter';
import { config } from './config';
import { resolveProfile } from './games';
import { RevealAnimation } from './animation/RevealAnimation';
import { DrawSequencer } from './animation/DrawSequencer';
import { BallSystem } from './machine/BallSystem';
import { SelectionZone } from './machine/SelectionZone';
import { loadMachine, cloneMachine } from './machine/MachineLoader';
import { buildMachineMapping, type MachineMapping } from './machine/MachineMapping';
import { addTopCapLabel } from './machine/TopCapLabel';
import { loadEnvironment } from './scene/Environment';
import { SceneRig } from './scene/SceneRig';
import type { GameProfile, LotteryEvent, LotteryMachineOptions } from './types';

type LotteryEventMap = {
  ready: { profile: GameProfile };
  /**
   * Fires during init() while assets stream in, then once more when the parse/build phase
   * starts. Hosts use this to drive a loading bar.
   *
   * - phase 'download': real byte progress. `total` may be 0 until Content-Length arrives.
   * - phase 'prepare': scene parse / build / first render. `ratio` is always 1 (indeterminate).
   */
  loadProgress: { phase: 'download' | 'prepare'; loaded: number; total: number; ratio: number };
  drawStart: { results: number[] };
  ballRevealed: { id: number; machineIndex: number };
  drawComplete: { results: number[] };
  error: { error: Error };
};

interface MachineUnit {
  /** Group hosting this machine's GLB instance + its ball system + selection zone. */
  group: Group;
  mapping: MachineMapping;
  balls: BallSystem;
  zone: SelectionZone;
  reveal: RevealAnimation;
}

const MODEL_PATH = 'models/lottery-machine.glb';
const HDRI_PATH = 'hdri/studio_small_03_1k.hdr';

/**
 * The top-level public API. Construct one of these per visual container.
 * The Pixi.js host (or any host) calls .init(), .start(), .playDraw(), .dispose().
 */
export class LotteryMachine {
  private readonly container: HTMLElement;
  private readonly opts: LotteryMachineOptions;
  private readonly profile: GameProfile;
  private readonly emitter = new EventEmitter<LotteryEventMap>();
  private readonly mobileMode: boolean;

  private rig: SceneRig | null = null;
  private machines: MachineUnit[] = [];
  private sequencer: DrawSequencer | null = null;
  /** One HTML frame + label pair per machine — decorates the SceneRig WebGL inset slot. */
  private insetSlots: Array<{ frame: HTMLElement; label: HTMLElement }> = [];
  /** ResizeObserver that re-lays out the inset frames whenever the container changes size. */
  private insetResizeObserver: ResizeObserver | null = null;

  private running = false;
  private paused = false;
  private lastTime = 0;
  private rafHandle: number | null = null;
  private state: 'idle' | 'initializing' | 'ready' | 'drawing' | 'disposed' = 'idle';

  constructor(container: HTMLElement, opts: LotteryMachineOptions) {
    this.container = container;
    this.opts = opts;
    this.profile = resolveProfile(opts.game);
    this.mobileMode = opts.mobileMode ?? detectMobile();
  }

  // ------------------ public API ------------------

  on<E extends keyof LotteryEventMap>(event: E, handler: (payload: LotteryEventMap[E]) => void): () => void {
    return this.emitter.on(event, handler);
  }

  /** Subscribe to a typed union form of events (more convenient for some callers). */
  onEvent(handler: (evt: LotteryEvent) => void): () => void {
    const offs: Array<() => void> = [
      this.on('ready', () => handler({ type: 'ready' })),
      this.on('loadProgress', (p) =>
        handler({ type: 'loadProgress', phase: p.phase, loaded: p.loaded, total: p.total, ratio: p.ratio }),
      ),
      this.on('drawStart', (p) => handler({ type: 'drawStart', results: p.results })),
      this.on('ballRevealed', (p) => handler({ type: 'ballRevealed', id: p.id, machineIndex: p.machineIndex })),
      this.on('drawComplete', (p) => handler({ type: 'drawComplete', results: p.results })),
      this.on('error', (p) => handler({ type: 'error', error: p.error })),
    ];
    return () => offs.forEach((off) => off());
  }

  /** One-time async setup: load assets, build scene, attach to DOM. */
  async init(): Promise<void> {
    if (this.state !== 'idle') {
      console.warn(`init() called in state "${this.state}"; ignored.`);
      return;
    }
    this.state = 'initializing';
    try {
      // Lock the scene's aspect ratio so the canvas stays fully visible as the window
      // resizes — narrowing the window shrinks the canvas height proportionally rather
      // than cropping the scene horizontally. Sicbo's 3 side-by-side machines need a
      // wider aspect than the single-machine games.
      const aspect = this.profile.machineCount === 3 ? 2.2 : 1.3;
      document.documentElement.style.setProperty('--scene-aspect', String(aspect));

      this.rig = new SceneRig(this.container, { mobileMode: this.mobileMode });

      // Build the zoom-in inset frame inside the container. The bottom-center result chip
      // is no longer used — the inset's own label shows the revealed number.
      this.ensureInsetOverlay();

      const base = this.opts.assetBaseUrl?.replace(/\/$/, '') ?? '';
      const modelUrl = `${base}/${MODEL_PATH}`;
      const hdrUrl = `${base}/${HDRI_PATH}`;

      // Parallel: load HDRI + GLB with byte-accurate progress, emitting an aggregate
      // 'loadProgress' event so the host can drive a loading bar smoothly.
      let hdrLoaded = 0;
      let hdrTotal = 0;
      let gltfLoaded = 0;
      let gltfTotal = 0;
      const emitDownload = (): void => {
        const loaded = hdrLoaded + gltfLoaded;
        const total = hdrTotal + gltfTotal;
        const ratio = total > 0 ? Math.min(1, loaded / total) : 0;
        this.emitter.emit('loadProgress', { phase: 'download', loaded, total, ratio });
      };
      const [, loaded] = await Promise.all([
        loadEnvironment(this.rig.scene, this.rig.renderer, hdrUrl, (l, t) => {
          hdrLoaded = l;
          hdrTotal = t;
          emitDownload();
        }),
        loadMachine(modelUrl, (l, t) => {
          gltfLoaded = l;
          gltfTotal = t;
          emitDownload();
        }),
      ]);

      // Switch the bar to indeterminate "preparing scene" mode while we build everything.
      this.emitter.emit('loadProgress', {
        phase: 'prepare',
        loaded: 1,
        total: 1,
        ratio: 1,
      });

      // Build N machines according to the profile.
      this.machines = this.buildMachines(loaded.root, loaded.exitPath);

      // Frame camera + align floor.
      const overallSphere = computeOverallSphere(this.machines);
      const groundY = Math.min(...this.machines.map((m) => m.mapping.groundY));
      this.rig.alignFloor(groundY);
      this.rig.frameMachine({
        centerY: overallSphere.center.y,
        sphereRadius: overallSphere.radius,
        sicbo: this.profile.machineCount === 3,
      });

      // Sequencer wires all machines together.
      this.sequencer = new DrawSequencer({
        profile: this.profile,
        reveals: this.machines.map((m) => m.reveal),
        onDrawStart: (results) => {
          this.hideInset();
          this.emitter.emit('drawStart', { results });
        },
        onBallRevealed: (slot, id) => {
          this.showInsetForBall(slot, id);
          this.emitter.emit('ballRevealed', { id, machineIndex: slot });
        },
        onDrawComplete: (results) => this.emitter.emit('drawComplete', { results }),
      });

      this.state = 'ready';
      this.emitter.emit('ready', { profile: this.profile });

      if (this.opts.autoStart !== false) this.start();
    } catch (err) {
      this.state = 'idle';
      const error = err instanceof Error ? err : new Error(String(err));
      this.emitter.emit('error', { error });
      throw error;
    }
  }

  start(): void {
    if (this.running) return;
    if (!this.rig) throw new Error('LotteryMachine.start() called before init()');
    this.running = true;
    this.paused = false;
    this.lastTime = performance.now();
    this.tick();
  }

  pause(): void {
    this.paused = true;
  }

  resume(): void {
    if (!this.running) return;
    this.paused = false;
    this.lastTime = performance.now();
  }

  /**
   * Run a draw. results.length must equal profile.resultsPerDraw.
   * - Single-machine games: pass a single-element array like [17].
   * - Sicbo: pass three elements like [3, 6, 2] — one ball per machine.
   */
  async playDraw(results: number[]): Promise<void> {
    if (!this.sequencer) throw new Error('playDraw() before init()');
    if (this.state !== 'ready') {
      throw new Error(`playDraw() called in state "${this.state}"`);
    }
    this.state = 'drawing';
    // Send any previously-displayed balls back into their chambers, in parallel.
    // Fires-and-awaits inside playDraw so the intro churn is the natural cover for the return arc.
    try {
      await Promise.all(this.machines.map((m) => m.reveal.returnToPool()));
      await this.sequencer.run(results);
    } finally {
      this.state = 'ready';
    }
  }

  /** Reset all balls to a fresh scatter (useful between demos). */
  reset(): void {
    for (const m of this.machines) {
      m.reveal.forceResetDisplayedBall();
      m.balls.reset();
    }
    this.hideInset();
  }

  /** Profile being run. */
  getProfile(): GameProfile {
    return this.profile;
  }

  /** DEV: introspection for debugging. */
  _debugInfo(): unknown {
    return this.machines.map((m, i) => {
      const balls = Array.from(m.balls.allBalls);
      const positions = balls.map((b) => ({ id: b.id, x: +b.position.x.toFixed(2), y: +b.position.y.toFixed(2), z: +b.position.z.toFixed(2), s: b.state }));
      const minY = Math.min(...balls.map((b) => b.position.y));
      const maxY = Math.max(...balls.map((b) => b.position.y));
      const maxDist = Math.max(
        ...balls.map((b) => b.position.distanceTo(m.balls.chamberCenter)),
      );
      return {
        index: i,
        chamberCenter: m.mapping.chamberCenter.toArray().map((v) => +v.toFixed(2)),
        chamberRadius: +m.balls.chamberRadius.toFixed(2),
        ballRadius: +m.balls.ballRadius.toFixed(2),
        chamberTopY: +m.mapping.chamberTopY.toFixed(2),
        resultHolder: m.mapping.resultHolder?.toArray().map((v) => +v.toFixed(2)) ?? null,
        trackWaypoints: m.mapping.trackWaypoints.map((wp) => wp.toArray().map((v) => +v.toFixed(2))),
        displayedBallId: m.reveal.getDisplayedBallId(),
        ballCount: balls.length,
        ballY_min: +minY.toFixed(2),
        ballY_max: +maxY.toFixed(2),
        maxDistFromChamberCenter: +maxDist.toFixed(2),
        sampleBallPositions: positions.slice(0, 5),
      };
    });
  }

  dispose(): void {
    this.running = false;
    if (this.rafHandle !== null) cancelAnimationFrame(this.rafHandle);
    for (const m of this.machines) {
      m.balls.dispose();
      m.zone.dispose();
    }
    this.machines = [];
    this.rig?.dispose();
    this.rig = null;
    this.emitter.removeAll();
    this.insetResizeObserver?.disconnect();
    this.insetResizeObserver = null;
    for (const slot of this.insetSlots) {
      slot.frame.remove();
      slot.label.remove();
    }
    this.insetSlots = [];
    this.state = 'disposed';
  }

  // ------------------ internals ------------------

  private buildMachines(loadedRoot: Object3D, exitPath: Vector3[]): MachineUnit[] {
    if (!this.rig) throw new Error('SceneRig not initialised');
    const count = this.profile.machineCount;
    const units: MachineUnit[] = [];

    // First machine uses the loaded root; clones for any further machines.
    const sources: Object3D[] = [loadedRoot];
    for (let i = 1; i < count; i++) sources.push(cloneMachine(loadedRoot));

    // Compute reference size from the first instance to know how far to space copies.
    const refMapping = buildMachineMapping(sources[0]!);
    const refDiameter = refMapping.contentSphere.radius * 2;
    const spacing = refDiameter * config.machine.sicboSpacingFactor;

    // Re-walk mapping per machine after positioning (so world transforms are correct).
    for (let i = 0; i < count; i++) {
      const group = new Group();
      group.name = `machine-${i}`;
      const sourceRoot = sources[i]!;
      group.add(sourceRoot);

      // X-offset so machines stand side-by-side, centered overall.
      const x = (i - (count - 1) / 2) * spacing;
      group.position.x = x;

      this.rig.contentRoot.add(group);
      group.updateMatrixWorld(true);

      const mapping = buildMachineMapping(group);
      const chamberR = mapping.chamberSphere.radius * (1 - config.balls.wallInset);

      const balls = new BallSystem({
        profile: this.profile,
        chamberCenter: mapping.chamberCenter,
        chamberRadius: chamberR,
        parent: this.rig.contentRoot,
        mobileMode: this.mobileMode,
        machineIndex: i,
      });

      const zone = new SelectionZone({
        parent: this.rig.contentRoot,
        chamberCenter: mapping.chamberCenter,
        chamberRadius: mapping.chamberSphere.radius,
        chamberTopY: mapping.chamberTopY,
      });
      zone.group.visible = false;

      // Baked exit path is in the original loaded scene's local space. Translate by this
      // machine's group offset so the path matches the machine's actual world position.
      const offset = new Vector3().setFromMatrixPosition(group.matrixWorld);
      const exitPathWorld = exitPath.map((p) => p.clone().add(offset));

      const reveal = new RevealAnimation(
        balls,
        zone,
        mapping.resultHolder,
        mapping.trackWaypoints,
        exitPathWorld,
        mapping.selectorNode,
        mapping.selectorRestPos,
        mapping.selectorRestScale,
        mapping.selectorMeshLocalTopY,
        mapping.chamberTopY,
      );

      units.push({ group, mapping, balls, zone, reveal });

      // Cover the GLB's baked logo on the top cap with an AMG sticker. Runs
      // per-machine so each clone in Sicbo also gets its label.
      addTopCapLabel(group, { text: 'AMG' });
    }

    return units;
  }

  /**
   * Create one HTML frame + label per machine, plus the matching WebGL inset rects in SceneRig.
   * Single-machine games get one inset top-right. Sicbo (3 machines) gets three insets across
   * the top of the container — each centered horizontally over its machine.
   *
   * Elements are created here once; their pixel positions/sizes are computed and applied
   * by layoutInsetOverlay(), which re-runs on every container resize.
   */
  private ensureInsetOverlay(): void {
    if (!this.rig) return;
    const count = this.profile.machineCount;
    const borderW = 3;

    for (let i = 0; i < count; i++) {
      const frame = document.createElement('div');
      frame.className = 'inset-frame';
      frame.style.cssText = [
        'position:absolute',
        'box-sizing:border-box',
        `border:${borderW}px solid #f7c948`,
        'border-radius:14px 14px 0 0',
        'box-shadow:0 8px 24px rgba(0,0,0,0.25)',
        'pointer-events:none',
        'z-index:5',
        'opacity:0',
        'transition:opacity 0.25s ease, border-color 0.2s ease',
      ].join(';');

      const label = document.createElement('div');
      label.className = 'inset-label';
      label.style.cssText = [
        'position:absolute',
        'box-sizing:border-box',
        'background:#f7c948',
        'color:#111',
        'border-radius:0 0 14px 14px',
        'font-family:-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,sans-serif',
        'font-weight:800',
        'display:flex',
        'align-items:center',
        'justify-content:center',
        'letter-spacing:0.04em',
        'box-shadow:0 8px 24px rgba(0,0,0,0.25)',
        'pointer-events:none',
        'z-index:5',
        'opacity:0',
        'transition:opacity 0.25s ease, background 0.2s ease, color 0.2s ease',
        'font-variant-numeric:tabular-nums',
      ].join(';');

      this.container.appendChild(frame);
      this.container.appendChild(label);
      this.insetSlots.push({ frame, label });
    }

    this.layoutInsetOverlay();
    // Re-flow whenever the container shrinks/grows so 3 sicbo insets always fit and the
    // single-machine inset stays anchored to the canvas's actual top-right.
    this.insetResizeObserver = new ResizeObserver(() => this.layoutInsetOverlay());
    this.insetResizeObserver.observe(this.container);
  }

  /**
   * Compute pixel positions/sizes for each inset frame and label based on the container's
   * current width, and apply them to both the WebGL inset rects (via SceneRig.configureInsets)
   * and the HTML overlay styles. Called once at init and whenever the container resizes.
   */
  private layoutInsetOverlay(): void {
    if (!this.rig || this.insetSlots.length === 0) return;
    const count = this.profile.machineCount;
    const containerW = this.container.clientWidth;
    if (containerW === 0) return;

    const sideMargin = 24;
    const gap = 16;
    // Largest inset we'd ever want, capped down if the container is small.
    const idealSize = count === 1 ? 280 : 220;
    const minSize = 96;
    const widthBudget = containerW - 2 * sideMargin - (count - 1) * gap;
    const size = Math.max(minSize, Math.min(idealSize, Math.floor(widthBudget / count)));

    // Label scales with inset so the digit stays proportional on small windows too.
    const labelHeight = Math.round(size * (count === 1 ? 72 / 280 : 56 / 220));
    const fontSize = Math.round(size * (count === 1 ? 42 / 280 : 32 / 220));
    // Top offset clears the controls bar.
    const top = count === 1 ? 96 : 80;

    const totalW = count * size + (count - 1) * gap;
    const startX =
      count === 1
        ? containerW - sideMargin - size
        : Math.max(sideMargin, Math.round((containerW - totalW) / 2));

    const rects: Array<{ x: number; y: number; w: number; h: number }> = [];
    for (let i = 0; i < count; i++) {
      rects.push({ x: startX + i * (size + gap), y: top, w: size, h: size });
    }
    this.rig.configureInsets(rects);

    for (let i = 0; i < count; i++) {
      const r = rects[i]!;
      const slot = this.insetSlots[i]!;
      slot.frame.style.top = `${r.y}px`;
      slot.frame.style.left = `${r.x}px`;
      slot.frame.style.width = `${r.w}px`;
      slot.frame.style.height = `${r.h}px`;
      slot.label.style.top = `${r.y + r.h}px`;
      slot.label.style.left = `${r.x}px`;
      slot.label.style.width = `${r.w}px`;
      slot.label.style.height = `${labelHeight}px`;
      slot.label.style.fontSize = `${fontSize}px`;
    }
  }

  private showInsetForBall(machineIndex: number, ballId: number): void {
    if (!this.rig) return;
    const unit = this.machines[machineIndex];
    const ui = this.insetSlots[machineIndex];
    if (!unit || !ui) return;

    const ball = unit.balls.getBall(ballId);
    this.rig.setInsetTarget(machineIndex, ball.position, unit.balls.ballRadius);

    const ballColor = this.profile.colorFor(ballId);
    const textColor = pickReadableTextColor(ballColor);
    ui.frame.style.borderColor = ballColor;
    ui.frame.style.opacity = '1';
    ui.label.textContent = String(ballId);
    ui.label.style.background = ballColor;
    ui.label.style.color = textColor;
    ui.label.style.opacity = '1';
  }

  private hideInset(): void {
    this.rig?.clearAllInsets();
    for (const slot of this.insetSlots) {
      slot.frame.style.opacity = '0';
      slot.label.style.opacity = '0';
    }
  }

  private tick = (): void => {
    if (!this.running) return;
    this.rafHandle = requestAnimationFrame(this.tick);
    const now = performance.now();
    const dt = Math.min(1 / 30, (now - this.lastTime) / 1000); // clamp big dt (background tabs)
    this.lastTime = now;
    if (!this.paused) {
      for (const m of this.machines) {
        m.balls.update(dt);
        m.zone.update(dt, now / 1000);
      }
    }
    this.rig!.render();
  };
}

// ------------------ helpers ------------------

function detectMobile(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Android|iPhone|iPad|iPod|Mobile|Tablet/i.test(navigator.userAgent);
}

/** Return '#111' or '#fff' depending on which gives better contrast against the given hex. */
function pickReadableTextColor(hex: string): string {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!m) return '#111';
  const v = parseInt(m[1]!, 16);
  const r = (v >> 16) & 0xff;
  const g = (v >> 8) & 0xff;
  const b = v & 0xff;
  // Rec. 601 luminance, normalised to [0,1].
  const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return lum > 0.55 ? '#111' : '#fff';
}

function computeOverallSphere(machines: MachineUnit[]): Sphere {
  const total = new Sphere();
  if (machines.length === 0) return total;
  // Each mapping.contentSphere is already in world space (built after group.updateMatrixWorld()).
  total.copy(machines[0]!.mapping.contentSphere);
  for (let i = 1; i < machines.length; i++) {
    total.union(machines[i]!.mapping.contentSphere);
  }
  return total;
}
